import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';

const normalize = (value = '') => value?.toLowerCase?.() || '';

const SellerClients = () => {
  const auth = getAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const initialForm = {
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    projectLocation: '',
    address: '',
  };
  const [form, setForm] = useState(initialForm);

  const fetchClients = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    const email = auth.currentUser?.email?.toLowerCase();
    if (!uid && !email) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, 'clients'));
      const data = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((client) => {
          const ownerUid = client.ownerUid || client.createdByUid || null;
          const assignedUid =
            client.assignedSellerUid ||
            client.assignedToUid ||
            client.salesRepUid ||
            null;
          const assignedEmail =
            normalize(client.assignedSellerEmail) ||
            normalize(client.assignedToEmail) ||
            '';
          return (
            ownerUid === uid ||
            assignedUid === uid ||
            (assignedEmail && email && assignedEmail === email)
          );
        });
      data.sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));
      setClients(data);
    } catch (err) {
      console.error('❌ Error cargando clientes del vendedor:', err);
      setError('No pudimos cargar tus clientes asignados.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = useMemo(() => {
    if (!search) return clients;
    const term = search.toLowerCase();
    return clients.filter(
      (client) =>
        client.businessName?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.includes(term)
    );
  }, [clients, search]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resolveClientDocId = async (email) => {
    const snap = await getDocs(query(collection(db, 'clients'), where('email', '==', email)));
    if (!snap.empty) {
      return snap.docs[0].id;
    }
    return email;
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!auth.currentUser?.email) return;
    const email = form.email.trim().toLowerCase();
    if (!email) {
      setCreateError('El correo es obligatorio.');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const callable = httpsCallable(functions, 'createClientFromAdmin');
      await callable({
        email,
        name: form.contactName || form.businessName,
        phone: form.phone,
        businessName: form.businessName,
        address: form.projectLocation || form.address || '',
      });

      const docId = await resolveClientDocId(email);
      const payload = {
        businessName: form.businessName,
        name: form.contactName || form.businessName,
        email,
        phone: form.phone,
        projectLocation: form.projectLocation,
        address: form.address || form.projectLocation || '',
        ownerUid: auth.currentUser?.uid || null,
        ownerEmail: auth.currentUser?.email?.toLowerCase() || null,
        ownerName: auth.currentUser?.displayName || auth.currentUser?.email || null,
        assignedSellerUid: auth.currentUser?.uid || null,
        assignedSellerEmail: auth.currentUser?.email?.toLowerCase() || null,
        assignedSellerName: auth.currentUser?.displayName || auth.currentUser?.email || null,
        source: 'seller-panel',
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      if (docId) {
        await setDoc(doc(db, 'clients', docId), payload, { merge: true });
      }

      try {
        await fetch('https://createresetlink-xjpm53xzha-uc.a.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
      } catch (err) {
        console.warn('No se pudo enviar el enlace de credenciales:', err.message);
      }

      await fetchClients();
      setShowModal(false);
      setForm(initialForm);
    } catch (err) {
      console.error('❌ Error creando cliente desde vendedor:', err);
      setCreateError(err.message || 'No pudimos crear el cliente.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Clientes asignados</h2>
          <p className="text-gray-500">
            Solo ves los clientes que fueron creados por ti o asignados a tu usuario.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
            placeholder="Buscar por nombre, email o teléfono"
          />
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
          >
            Nuevo cliente
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Cargando clientes…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500">No encontramos clientes con esos criterios.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((client) => (
            <article
              key={client.id}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900">
                {client.businessName || client.name || 'Cliente sin nombre'}
              </h3>
              <p className="text-sm text-gray-500">{client.email}</p>
              <p className="text-sm text-gray-500">{client.phone || 'Sin teléfono'}</p>
              <p className="text-xs text-gray-400">{client.address || client.projectLocation || ''}</p>
              <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-500">
                <p>Origen: {client.source || 'Manual'}</p>
                {client.ownerUid && (
                  <p>
                    Propietario del registro:{' '}
                    {client.ownerUid === auth.currentUser?.uid ? 'Tú' : client.ownerUid}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>

    {showModal && (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
        <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-gray-400">Nuevo cliente</p>
                <h3 className="text-2xl font-semibold text-gray-900">Registra tu contacto</h3>
                <p className="text-sm text-gray-500">
                  El sistema enviará un correo para que el cliente active sus credenciales.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setForm(initialForm);
                  setCreateError('');
                }}
                className="text-sm font-semibold text-gray-400 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>
            <form className="mt-6 space-y-4" onSubmit={handleCreateClient}>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Nombre del negocio</label>
                  <input
                    type="text"
                    name="businessName"
                    value={form.businessName}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Persona contacto</label>
                  <input
                    type="text"
                    name="contactName"
                    value={form.contactName}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Correo</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Ubicación / proyecto</label>
                  <input
                    type="text"
                    name="projectLocation"
                    value={form.projectLocation}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Dirección</label>
                  <input
                    type="text"
                    name="address"
                    value={form.address}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>
              {createError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  {createError}
                </div>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  El cliente recibirá instrucciones por email para crear sus credenciales.
                </p>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {creating ? 'Creando…' : 'Guardar cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
    )}
    </>
  );
};

export default SellerClients;
